import { Button, Input, Text, VStack } from '@chakra-ui/react';
import type { UserPermission } from '../../../../../hooks/useEditDefaultPermissions';
import type { Role } from '../../../../../hooks/useValidateSession';
import SearchableSingleSelect from '../../../../../components/SearchableSingleSelect';
import LocationSelector from '../../../../../components/LocationSelector';
import type { InvitePositionOption } from '../../../../../utils/invitePositionOptions';
import {
    SettingsCard,
    SettingsFlowForm,
    SettingsFlowSplit,
    SettingsRow,
} from '../../../../../components/settings/primitives';
import { copyUserPermission } from '../../permissions/permissionCatalog';
import InvitePermissionsEditor from './InvitePermissionsEditor';
import {
    INVITE_PERMISSIONS_OPTIONS,
    INVITE_ROLE_OPTIONS,
    effectiveInvitePermissions,
    invitePermissionsMode,
    isValidInviteEmail,
    type InviteDraft,
    type InvitePermissionsMode,
    type InviteRoleValue,
} from './inviteDraft';

export interface AddUserSingleStepProps {
    draft: InviteDraft;
    onChange: (next: InviteDraft) => void;
    positionOptions: InvitePositionOption[];
    /** Invoicing-only orgs have exactly one position, so the field is locked. */
    isPositionLocked?: boolean;
    defaultPermissions: Partial<Record<Role, UserPermission>>;
}

/**
 * Details step for adding one user: the invite fields, and — once permissions
 * are set to Custom — the role defaults opened up beside them as a matrix the
 * admin can change before the invite goes out. Default permissions need no
 * second pane, so the form stays a single centered column until it is asked
 * for.
 */
const AddUserSingleStep = ({
    draft,
    onChange,
    positionOptions,
    isPositionLocked = false,
    defaultPermissions,
}: AddUserSingleStepProps) => {
    const isAdmin = draft.role === 'admin';
    const mode = isAdmin ? 'default' : invitePermissionsMode(draft);
    const emailTouched = draft.email.trim().length > 0;
    const showEmailError = emailTouched && !isValidInviteEmail(draft.email);

    const handlePermissionsMode = (next: InvitePermissionsMode) => {
        onChange({
            ...draft,
            // Custom starts from the position's defaults, so the matrix opens on
            // what this user would have got anyway.
            customPermissions:
                next === 'custom' ? effectiveInvitePermissions(draft, defaultPermissions) : null,
        });
    };

    const handleRole = (next: InviteRoleValue) => {
        // Admins hold every tab and permission, so a custom set is meaningless.
        onChange({
            ...draft,
            role: next,
            customPermissions: next === 'admin' ? null : draft.customPermissions,
        });
    };

    const handlePosition = (next: Role) => {
        // A custom set was seeded from the old position's defaults; keeping it
        // would silently attach the wrong baseline, so it resets with the role.
        onChange({ ...draft, position: next, customPermissions: null });
    };

    const fields = (
        <VStack spacing={6} align="stretch">
            <SettingsCard title="User">
                <SettingsRow
                    label="Email"
                    description="The invitation is sent here with signup instructions."
                    control={
                        <VStack align="stretch" spacing={1} w="260px" maxW="100%">
                            <Input
                                size="sm"
                                h="36px"
                                type="email"
                                placeholder="user@example.com"
                                value={draft.email}
                                isInvalid={showEmailError}
                                onChange={(e) => onChange({ ...draft, email: e.target.value })}
                            />
                            {showEmailError && (
                                <Text fontSize="xs" color="red.500">
                                    Enter a valid email address.
                                </Text>
                            )}
                        </VStack>
                    }
                />
                <SettingsRow
                    label="Position"
                    description="Determines the default tabs and permissions."
                    control={
                        <SearchableSingleSelect
                            width="200px"
                            options={positionOptions}
                            value={draft.position}
                            placeholder="Select position"
                            isDisabled={isPositionLocked}
                            onChange={(next) => handlePosition(next as Role)}
                        />
                    }
                />
                <SettingsRow
                    label="Locations"
                    description="Which shops this user can see and work in."
                    control={
                        <LocationSelector
                            selectedLocations={draft.locationId}
                            onLocationChange={(locationId) => onChange({ ...draft, locationId })}
                            width="200px"
                        />
                    }
                />
            </SettingsCard>

            <SettingsCard title="Access">
                <SettingsRow
                    label="Role"
                    description="Admins have full access to every tab, permission, and setting."
                    control={
                        <SearchableSingleSelect
                            width="200px"
                            options={INVITE_ROLE_OPTIONS}
                            value={draft.role}
                            onChange={(next) => handleRole(next as InviteRoleValue)}
                        />
                    }
                />
                <SettingsRow
                    label="Permissions"
                    description={
                        isAdmin
                            ? 'Admins always have everything.'
                            : 'Default follows the position; Custom opens the list to change.'
                    }
                    control={
                        <SearchableSingleSelect
                            width="200px"
                            options={INVITE_PERMISSIONS_OPTIONS}
                            value={mode}
                            isDisabled={isAdmin || !draft.position}
                            onChange={(next) => handlePermissionsMode(next as InvitePermissionsMode)}
                        />
                    }
                />
            </SettingsCard>
        </VStack>
    );

    if (mode !== 'custom' || !draft.customPermissions) {
        return <SettingsFlowForm>{fields}</SettingsFlowForm>;
    }

    return (
        <SettingsFlowSplit
            leftTitle="User details"
            left={fields}
            rightTitle="Custom permissions"
            rightActions={
                <Button
                    size="sm"
                    variant="outlineGray"
                    onClick={() =>
                        onChange({
                            ...draft,
                            customPermissions: copyUserPermission(
                                draft.position ? defaultPermissions[draft.position] : undefined,
                            ),
                        })
                    }
                >
                    Reset to defaults
                </Button>
            }
            right={
                <InvitePermissionsEditor
                    value={draft.customPermissions}
                    onChange={(customPermissions) => onChange({ ...draft, customPermissions })}
                />
            }
        />
    );
};

export default AddUserSingleStep;
